/**
 * lib/wsTicket.ts -- every WebSocket authenticates with a single-use ticket.
 *
 * PURE: no imports. Executed by _test/ws-ticket.mjs.
 *
 * WHY (router B6: AUTHN-15 / IDOR-17).
 *
 * The SPA minted a ticket and, on ANY failure, fell back to
 * `?token=<session JWT>`. A full session token in a URL lands in browser
 * history, edge access logs and Logpush. The router now refuses `?token=` on
 * /ws, /ws/events and /api/channels/:ch/ws (401 TOKEN_IN_URL_REFUSED), so the
 * fallback is both a leak and dead. There is no fallback: no ticket, no
 * socket -- and the status says why.
 *
 * Tickets (POST /ws/ticket -> { ticket, expires_in: 60 }) are SINGLE-USE: the
 * router consumes the jti at the handshake (WS_TICKET_USED on reuse) and they
 * expire after 60 s. So a ticket is minted immediately before EVERY connect
 * and reconnect, never cached and never reused. TickStreamDO closes a socket
 * with 4001 once the session behind it has expired; the reconnect then mints
 * again, and a 401 from /ws/ticket means the SESSION is gone, which retrying
 * cannot fix.
 */

export type TicketResult =
  | { ok: true; ticket: string }
  | { ok: false; fatal: boolean; reason: string };

/** Minimal shape of a thrown HttpError (lib/api.ts) or a network error. */
interface ErrLike { status?: unknown; message?: unknown }

/**
 * Mint one ticket. `post` performs POST /ws/ticket and resolves to the parsed
 * body, or throws (HttpError with .status, or a network error without one).
 *
 * fatal = retrying will not help (no session, no entitlement): stop
 * reconnecting and tell the user. Everything else (503 WS_TICKET_UNAVAILABLE,
 * a network blip, a malformed body) is retried with backoff.
 */
export async function mintWsTicket(post: () => Promise<unknown>): Promise<TicketResult> {
  try {
    const body = await post() as { ticket?: unknown } | null;
    const t = body && typeof body === 'object' ? body.ticket : undefined;
    if (typeof t === 'string' && t.length > 0) return { ok: true, ticket: t };
    // A 200 without a ticket is not a ticket. Do not connect on it.
    return { ok: false, fatal: false, reason: 'The live-feed ticket service answered without a ticket.' };
  } catch (e) {
    const err = e as ErrLike;
    const status = typeof err?.status === 'number' ? err.status : null;
    const msg = typeof err?.message === 'string' ? err.message : String(e);
    if (status === 401) return { ok: false, fatal: true, reason: 'Session expired -- sign in again to restore the live feed.' };
    if (status === 403) return { ok: false, fatal: true, reason: 'Your plan does not include the live feed (terminal:view).' };
    return { ok: false, fatal: false, reason: status ? `Live-feed ticket unavailable (HTTP ${status}): ${msg}` : `Live-feed ticket request failed: ${msg}` };
  }
}

/**
 * The socket URL for one connect. The ticket goes in `?ticket=` and nowhere
 * else; any `token` (or stale `ticket`) parameter on the base or in `params`
 * is removed rather than sent.
 */
export function wsUrlWithTicket(base: string, ticket: string, params: Record<string, string> = {}): string {
  if (!ticket) throw new Error('wsUrlWithTicket: a ticket is required -- there is no token fallback');
  const q = base.indexOf('?');
  const path = q === -1 ? base : base.slice(0, q);
  const sp = new URLSearchParams(q === -1 ? '' : base.slice(q + 1));
  for (const [k, v] of Object.entries(params)) sp.set(k, v);
  sp.delete('token');
  sp.delete('ticket');
  sp.set('ticket', ticket);
  return `${path}?${sp.toString()}`;
}

/** Router close code for "the session behind this socket expired" (TickStreamDO). */
export const WS_CLOSE_SESSION_EXPIRED = 4001;
